import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DashboardWidgetType } from 'src/constants/dashboard';
import type { DashboardWidgetConfig, Tag } from 'src/types/dashboard';
import useDebounce, { debounceTimes } from 'src/hooks/useDebounce';
import { dashboardContainsWidgetTypes } from 'src/components/dashboard/DashboardContext/helper';
import type { NoData } from 'src/components/dashboard/hooks/helper';
import { getNoData } from 'src/components/dashboard/hooks/helper';
import type { SentimentFilterValues } from 'src/components/dashboard/hooks/useTagsQuery';
import useTagsQuery, {
  TagSentimentFilter,
} from 'src/components/dashboard/hooks/useTagsQuery';

import { useDashboardContext } from '../index';

export interface TagCloudContextValue {
  tagResults: Tag[];
  toggleTranslation: () => void;
  translated: boolean;
  textFilter: string;
  textFilterLoading: boolean;
  setTextFilter: Dispatch<SetStateAction<string>>;
  setSentimentFilter: Dispatch<SetStateAction<SentimentFilterValues>>;
  sentimentFilter: SentimentFilterValues;
  errorMsg: string;
  loading: boolean;
  pending: boolean;
  noData: NoData;
}

export const TagCloudContext = createContext<TagCloudContextValue>({
  tagResults: [],
  toggleTranslation: () => {},
  translated: true,
  setTextFilter: () => {},
  textFilter: '',
  textFilterLoading: false,
  setSentimentFilter: () => {},
  sentimentFilter: TagSentimentFilter.ALL,
  errorMsg: '',
  loading: false,
  pending: false,
  noData: { initial: false, filtered: false },
});

const TagWidgets = [DashboardWidgetType.TAG_CLOUD];

export const TagCloudContextProvider: FC<{
  config?: DashboardWidgetConfig;
  children?: ReactNode;
}> = ({ config, children }) => {
  const { t } = useTranslation('common');
  const { dashboardConfig } = useDashboardContext();
  const [textFilter, setTextFilter] = useState('');
  const [translated, setTranslated] = useState(true);
  const debouncedTextFilter = useDebounce(
    textFilter,
    debounceTimes.TEXT_FILTER,
  );
  const [sentimentFilter, setSentimentFilter] = useState<SentimentFilterValues>(
    TagSentimentFilter.ALL,
  );
  const isEnabled = useMemo(
    () => dashboardContainsWidgetTypes(dashboardConfig, TagWidgets),
    [dashboardConfig],
  );
  const { data, isFetching, isLoading, error } = useTagsQuery(
    isEnabled,
    config ? config.questions?.[0] : null,
    dashboardConfig?.id,
    debouncedTextFilter,
    sentimentFilter,
  );
  const loading = isFetching || isLoading;
  const textFilterLoading = textFilter !== debouncedTextFilter;

  const tagResults = useMemo(
    () => (translated ? data?.translated : data?.default_language) || [],
    [translated, data],
  );

  const toggleTranslation = () => setTranslated((prevState) => !prevState);

  const noData = useMemo(
    () =>
      getNoData(
        loading,
        !!tagResults.length,
        sentimentFilter === TagSentimentFilter.ALL &&
          debouncedTextFilter === '',
      ),
    [loading, tagResults, sentimentFilter, debouncedTextFilter],
  );

  const errorMsg = useMemo(() => {
    if (!error) {
      // if noData is not set but there are still no tags in one of the pools an error is returned
      if (!noData && !tagResults.length) {
        return t('noResults');
      }
      return '';
    }

    switch (error) {
      case 404:
        return t('pageNotFound.headline');
      case 401:
        return t('pageNotFound.accessDenied');
      default:
        return t('unknownError');
    }
  }, [error, tagResults, noData]);

  const pending = useMemo(
    () => isEnabled && isLoading && !tagResults.length,
    [isEnabled, tagResults],
  );

  return (
    <TagCloudContext.Provider
      value={{
        tagResults,
        toggleTranslation,
        translated,
        setTextFilter,
        textFilter,
        textFilterLoading,
        setSentimentFilter,
        sentimentFilter,
        errorMsg,
        loading,
        pending,
        noData,
      }}
    >
      {children}
    </TagCloudContext.Provider>
  );
};

export const useTagCloud = () => useContext(TagCloudContext);
